import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    catchError,
    concatMap,
    exhaustMap,
    map,
    mergeAll,
    mergeMap,
    Observable,
    of,
    switchMap,
    withLatestFrom,
} from 'rxjs';
import { v4 } from 'uuid';
import { ModalWindowsService } from '../../../modals/services/modal-windows.service';
import {
    InvitationExternalIdType,
    InvitationType,
} from '../../../profile/models/enums/profile.enum';
import { InvitationService } from '../../../profile/services/invitation.service';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { AppState } from '../../../shared/store/appState';
import { TournamentResult } from '../../models/api/responses/tournamentGeneral.response';
import {
    ClosestTournamentMatch,
    DeleteParticipantResponse,
    JoinAsTeamTournamentResponse,
    JoinTeamTournamentAsSinglePlayerResponse,
    JoinTournamentGoodResponse,
    RemoveTeamTournamentMemberResponse,
    TeamTournamentResponse,
    TournamentParticipantsResponse,
    TournamentPendingParticipantsResponse,
} from '../../models/api/responses/tournamentParticipants.response';
import { TeamTournament } from '../../models/tournaments.interface';
import { TournamentParticipantService } from '../../services/tournament-participants.service';
import { TournamentGeneralActions, TournamentParticipantsActions } from '../actions/actions-type.action';
import { TournamentGeneralSelectors } from '../selectors/selectors-type.selector';
import { PAGE_SIZE } from 'src/app/utils/app.constants';
import { CommunicationChatActions } from '../../../communication-chat/store/actions/actions-type.action';
import { WalletActions } from '../../../wallet/store/actions/actions-type.action';

@Injectable()
export class TournamentParticipantsEffect {
    private store = inject<Store<AppState>>(Store);
    private actions$ = inject(Actions);
    private tournamentParticipantService = inject(TournamentParticipantService);
    private invitationService = inject(InvitationService);
    private notificationsService = inject(NotificationsService);
    private modalService = inject(ModalWindowsService);

    fetchParticipants$ = createEffect(()=>
    this.actions$.pipe(
        ofType(
            TournamentParticipantsActions.fetchTournamentParticipants,
        ),
        switchMap(({ payload }) =>
            this.tournamentParticipantService
                .fetchTournamentParticipants(payload.tournamentId, payload.page, payload.pageSize, payload.IsAdminOrTournamentOwner)
                .pipe(
                    map((response: TournamentParticipantsResponse) =>
                        TournamentParticipantsActions.fetchTournamentParticipantsSuccess(
                            {
                                payload: response,
                            }
                        )
                    ),
                    catchError((response: TournamentParticipantsResponse) =>
                        of(
                            TournamentParticipantsActions.fetchTournamentParticipantsFailure()
                        )
                    )
                )
        )
    ));
    fetchPendingParticipants$ = createEffect(()=>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.fetchTournamentPendingParticipants,
            ),
            switchMap(({ payload }) =>
                this.tournamentParticipantService
                    .fetchTournamentPendingParticipants(payload.tournamentId, payload.page, payload.pageSize, payload.IsAdminOrTournamentOwner)
                    .pipe(
                        map((response: TournamentPendingParticipantsResponse) =>
                            TournamentParticipantsActions.fetchTournamentPendingParticipantsPagingSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: TournamentPendingParticipantsResponse) =>
                            of(
                                TournamentParticipantsActions.fetchTournamentPendingParticipantsFailure()
                            )
                        )
                    )
            )
        ));
    fetchTournamentParticipants$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.connectPlayersToTheTournamentSuccess,
                TournamentParticipantsActions.connectTeamsToTheTournamentSuccess,
                TournamentParticipantsActions.connectMixParticipantsToTheTournamentSuccess,
            ),
            switchMap(({ tournamentId }) =>
                    this.tournamentParticipantService
                    .fetchTournamentPendingParticipants(tournamentId, 1, PAGE_SIZE, false)
                    .pipe(
                        map((response: TournamentPendingParticipantsResponse) =>
                            TournamentParticipantsActions.fetchTournamentPendingParticipantsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: TournamentParticipantsResponse) =>
                            of(
                                TournamentParticipantsActions.fetchTournamentPendingParticipantsFailure()
                            )
                        )
                    )
            )
        )
    );
    fetchTournamentPendingParticipants$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.connectPlayersToTheTournamentSuccess,
                TournamentParticipantsActions.connectTeamsToTheTournamentSuccess,
                TournamentParticipantsActions.connectMixParticipantsToTheTournamentSuccess,
            ),
            switchMap(({ tournamentId }) =>
                    this.tournamentParticipantService
                    .fetchTournamentPendingParticipants(tournamentId, 1, PAGE_SIZE, false)
                    .pipe(
                        map((response: TournamentPendingParticipantsResponse) =>
                            TournamentParticipantsActions.fetchTournamentPendingParticipantsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: TournamentPendingParticipantsResponse) =>
                            of(
                                TournamentParticipantsActions.fetchTournamentPendingParticipantsFailure()
                            )
                        )
                    )
            )
        )
    );

    joinTournament$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.joinToATournament),
            exhaustMap(({ payload }) =>
                this.tournamentParticipantService
                    .joinToATournament(payload)
                    .pipe(
                        mergeMap((response: JoinTournamentGoodResponse) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.JOINED_TOURNAMENT,
                                type: SnackBarType.Success,
                            });

                            return [
                                TournamentParticipantsActions.joinToATournamentSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                TournamentGeneralActions.getTournamentById(
                                    {
                                        payload: payload.tournamentId
                                    }
                                ),
                                CommunicationChatActions.fetchChatsByTargetId({
                                    payload: {
                                        targetId: payload.tournamentId
                                    }
                                })
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.joinToATournamentFailure()
                            );
                        })
                    )
            )
        )
    );

    joinAsTeamTournament$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.joinAsTeamToTournament),
            exhaustMap((action) => {
                const { tournamentId, playerId, ...data } = action.payload;
                if (!tournamentId || !playerId) {
                    return of();
                }

                return this.tournamentParticipantService
                    .joinAsTeamToATournament(tournamentId, data)
                    .pipe(
                        concatMap((response: JoinAsTeamTournamentResponse) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.JOINED_TOURNAMENT,
                                type: SnackBarType.Success,
                            });

                            // this.modalService.openMyTeamTournament(
                            //     response.participantId,
                            //     tournamentId,
                            //     playerId,
                            //     null
                            // );

                            return [
                                TournamentParticipantsActions.joinAsTeamToTournamentSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                TournamentParticipantsActions.sendInvitationToJoinTeamTournament(
                                    {
                                        payload: {
                                            tournamentId,
                                            participantId:
                                                response.participantId,
                                            invitedMembers:
                                                data.invitedMembers,
                                            currentPlayerId: playerId,
                                            isV2: data.isV2
                                        },
                                    }
                                ),
                                TournamentGeneralActions.getTournamentById(
                                    {
                                        payload: tournamentId
                                    }
                                ),
                                CommunicationChatActions.fetchChatsByTargetId({
                                    payload: {
                                        targetId: tournamentId
                                    }
                                })
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.joinAsTeamToTournamentFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    fetchAvailableTeamToJoinAsSinglePlayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.fetchAvailableTeamToJoinAsSinglePlayer
            ),
            switchMap(({ payload }) =>
                this.tournamentParticipantService
                    .fetchAvailableTeamToJoin(payload.tournamentId)
                    .pipe(
                        map((response: TeamTournamentResponse) =>
                            TournamentParticipantsActions.fetchAvailableTeamToJoinAsSinglePlayerSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentParticipantsActions.fetchAvailableTeamToJoinAsSinglePlayerFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    joinTeamTournamentAsSinglePlayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.joinTeamTournamentAsSinglePlayer
            ),
            exhaustMap(({ payload }) => {
                const { tournamentId, participantId, requestData } = payload;
                const { playerProfileId } = requestData;

                return this.tournamentParticipantService
                    .joinTeamTournamentAsSinglePlayer(
                        tournamentId,
                        participantId,
                        requestData
                    )
                    .pipe(
                        mergeMap(
                            (
                                response: JoinTeamTournamentAsSinglePlayerResponse
                            ) => {
                                this.notificationsService.openSnackBar({
                                    title:
                                        NOTIFICATIONS.JOINED_AS_SINGLE_PLAYER +
                                        payload.teamName,
                                    type: SnackBarType.Success,
                                });
                                return [
                                    TournamentParticipantsActions.joinTeamTournamentAsSinglePlayerSuccess(
                                        {
                                            payload: response,
                                        }
                                    ),
                                    TournamentParticipantsActions.fetchJoiningTeamTournament(
                                        {
                                            payload: {
                                                tournamentId,
                                                playerId: playerProfileId,
                                            },
                                        }
                                    ),
                                    CommunicationChatActions.fetchChatsByTargetId({
                                        payload: {
                                            targetId: tournamentId
                                        }
                                    })
                                ];
                            }
                        ),

                        catchError((response: BadResponseWithError) =>{
                                this.notificationsService.openSnackBar({
                                    title: 'Error',
                                    message: response.error.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                            return                             of(
                                TournamentParticipantsActions.joinTeamTournamentAsSinglePlayerFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        }

                        )
                    );
            })
        )
    );

    getTeamTournament$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.fetchJoiningTeamTournament),
            concatMap(({ payload }) =>
                this.tournamentParticipantService
                    .getTeamTournamentsByPlayer(
                        payload.tournamentId,
                        payload.playerId
                    )
                    .pipe(
                        map((response: TeamTournamentResponse[]) =>
                            TournamentParticipantsActions.fetchJoiningTeamTournamentSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error?.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.fetchJoiningTeamTournamentFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    addCapitanToTeamTournament$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.addCapitanToTeamTournament),
            concatMap(({ payload }) =>
                this.tournamentParticipantService
                    .addCapitanToTeamTournament(
                        payload.tournamentId,
                        payload.participantId,
                        payload.capitanId,
                        payload.gameAccountId || '',
                        payload.position,
                    )
                    .pipe(
                        mergeMap((response) =>{
                            return [
                                TournamentParticipantsActions.addCapitanToTeamTournamentSuccess(
                                {
                                    payload: response,
                                }
                            ), TournamentParticipantsActions.fetchJoiningTeamTournament(
                                {
                                    payload: {
                                        tournamentId:
                                        payload.tournamentId,
                                        playerId:
                                        payload.capitanId,
                                    },
                                }
                            )]
                        }
                        ),
                        catchError((response: any) =>
                            of(
                                TournamentParticipantsActions.addCapitanToTeamTournamentFailure(
                                    {
                                        payload: response.error,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    invitePlayersToTeamTournament$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.sendInvitationToJoinTeamTournament
            ),
            concatMap(({ payload }) =>
                of(payload.capitanId).pipe(
                    switchMap((capitanId) => {
                        if (capitanId) {
                            this.store.dispatch(
                                TournamentParticipantsActions.addCapitanToTeamTournament(
                                    {
                                        payload: {
                                            tournamentId: payload.tournamentId,
                                            participantId:
                                                payload.participantId,
                                            capitanId,
                                            gameAccountId: payload.gameAccountId
                                        },
                                    }
                                )
                            );
                        }

                        return this.invitationService
                            .sendInvitations({
                                invitees: payload.invitedMembers,
                                targetType: InvitationType.TournamentTeam,
                                keyValues: [
                                    {
                                        externalIdType:
                                            InvitationExternalIdType.TournamentId,
                                        externalId: payload.tournamentId,
                                    },
                                    {
                                        externalIdType:
                                            InvitationExternalIdType.ParticipantId,
                                        externalId: payload.participantId,
                                    },
                                ],
                                requestId: v4(),
                                isTournamentV2: !!payload.isV2,
                            })
                            .pipe(
                                concatMap((response: any) => {
                                    this.notificationsService.openSnackBar({
                                        title: NOTIFICATIONS.INVITATIONS_SENT,
                                        type: SnackBarType.Success,
                                    });

                                    return [
                                        TournamentParticipantsActions.sendInvitationToJoinTeamTournamentSuccess(
                                            {
                                                payload: response,
                                            }
                                        ),
                                        TournamentParticipantsActions.fetchJoiningTeamTournament(
                                            {
                                                payload: {
                                                    tournamentId:
                                                        payload.tournamentId,
                                                    playerId:
                                                        payload.currentPlayerId,
                                                },
                                            }
                                        ),
                                    ];
                                }),
                                catchError((response: BadResponseWithError) => {
                                    this.notificationsService.openSnackBar({
                                        title: 'Error',
                                        message: response.error.message,
                                        type: SnackBarType.Error,
                                        error: response.error,
                                    });
                                    return of(
                                        TournamentParticipantsActions.sendInvitationToJoinTeamTournamentFailure(
                                            {
                                                payload: response,
                                            }
                                        )
                                    );
                                })
                            );
                    })
                )
            )
        )
    );

    cancelMemberInvitation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.cancelMemberInvitation),
            exhaustMap((action) =>
                this.invitationService
                    .updateStatus(
                        action.payload.invitationId,
                        action.payload.status
                    )
                    .pipe(
                        map((response) =>
                            TournamentParticipantsActions.cancelMemberInvitationSuccess(
                                {
                                    payload: {
                                        invitationId:
                                            action.payload.invitationId,
                                        source: action.payload.source,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentParticipantsActions.cancelMemberInvitationFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    connectPlayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.connectPlayersToTheTournament),
            withLatestFrom(
                this.store.select(
                    TournamentGeneralSelectors.selectCurrentTournament
                )
            ),
            switchMap(([action, tournament]) =>
                this.invitationService
                    .sendInvitations({
                        invitees: action.payload.playersIds,
                        targetType: InvitationType.SinglePlayerTournament,
                        keyValues: [
                            {
                                externalIdType:
                                    InvitationExternalIdType.TournamentId,
                                externalId: tournament.id,
                            },
                        ],
                        requestId: v4(),
                        isTournamentV2: action.payload.isV2,
                    })
                    .pipe(
                        map(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.INVITATIONS_SENT,
                                type: SnackBarType.Success,
                            });
                            return TournamentParticipantsActions.connectPlayersToTheTournamentSuccess(
                                {
                                    tournamentId: tournament.id,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.connectPlayersToTheTournamentFailure()
                            );
                        })
                    )
            )
        )
    );

    deleteParticipant$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.deletePlayerFromTheTournament),
            exhaustMap((action) =>
                this.tournamentParticipantService
                    .deleteTournamentParticipant({
                        player: action.payload,
                    })
                    .pipe(
                        mergeMap((response: DeleteParticipantResponse) => [
                            TournamentParticipantsActions.deletePlayerFromTheTournamentSuccess(
                                {
                                    participantId: response.participantId,
                                    tournamentId: response.tournamentId,
                                }
                            ),
                            TournamentParticipantsActions.fetchAvailableTeamToJoinAsSinglePlayer(
                                {
                                    payload: {
                                        tournamentId:
                                            action.payload.tournamentId,
                                    },
                                }
                            ),
                            TournamentGeneralActions.getTournamentById(
                                {
                                    payload: action.payload.tournamentId
                                    ,
                                }
                            ),

                        ]),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.deletePlayerFromTheTournamentFailure()
                            );
                        })
                    )
            )
        )
    );

    removeTeamTournamentMember$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.removeTeamTournamentMember),
            exhaustMap(({ payload: { request, shouldRemoveTeamTournament } }) =>
                this.tournamentParticipantService
                    .removeTeamTournamentMember(request)
                    .pipe(
                        mergeMap(
                            (response: RemoveTeamTournamentMemberResponse) => [
                                TournamentParticipantsActions.removeTeamTournamentMemberSuccess(
                                    {
                                        payload: {
                                            memberId: request.memberId,
                                            teamTournamentId:
                                                request.teamTournamentId,
                                            shouldRemoveTeamTournament:
                                                shouldRemoveTeamTournament ??
                                                false,
                                        },
                                    }
                                ),
                                TournamentParticipantsActions.fetchAvailableTeamToJoinAsSinglePlayer(
                                    {
                                        payload: {
                                            tournamentId: request.tournamentId,
                                        },
                                    }
                                ),
                            ]
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.removeTeamTournamentMemberFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    connectTeamsS = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.connectTeamsToTheTournament),
            withLatestFrom(
                this.store.select(
                    TournamentGeneralSelectors.selectCurrentTournament
                )
            ),
            exhaustMap(([action, tournament]) =>
                this.invitationService
                    .sendInvitations({
                        invitees: action.payload.teamIds,
                        targetType: InvitationType.TeamTournament,
                        keyValues: [
                            {
                                externalIdType:
                                    InvitationExternalIdType.TournamentId,
                                externalId: tournament.id,
                            },
                        ],
                        requestId: v4(),
                        isTournamentV2: action.payload.isV2,
                    })
                    .pipe(
                        map(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.INVITATIONS_SENT,
                                type: SnackBarType.Success,
                            });
                            return TournamentParticipantsActions.connectTeamsToTheTournamentSuccess(
                                {
                                    tournamentId: tournament.id,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.connectTeamsToTheTournamentFailure()
                            );
                        })
                    )
            )
        )
    );

    connectMixParticipantsS = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.connectMixParticipantsToTheTournament
            ),
            withLatestFrom(
                this.store.select(
                    TournamentGeneralSelectors.selectCurrentTournament
                )
            ),
            switchMap(([action, tournament]) =>
                this.invitationService
                    .sendInvitations({
                        invitees: action.payload.participantsIds,
                        targetType: InvitationType.MixTournament,
                        keyValues: [
                            {
                                externalIdType:
                                    InvitationExternalIdType.TournamentId,
                                externalId: tournament.id,
                            },
                        ],
                        requestId: v4(),
                        isTournamentV2: !!tournament.isTournamentv2
                    })
                    .pipe(
                        map(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.INVITATIONS_SENT,
                                type: SnackBarType.Success,
                            });
                            return TournamentParticipantsActions.connectMixParticipantsToTheTournamentSuccess(
                                {
                                    tournamentId: tournament.id,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });
                            return of(
                                TournamentParticipantsActions.connectMixParticipantsToTheTournamentFailure()
                            );
                        })
                    )
            )
        )
    );

    fetchTeamTournamentDetailsById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.fetchTeamTournamentDetails),
            switchMap((action) =>
                this.tournamentParticipantService
                    .getTeamTournamentInfo(
                        action.payload.tournamentId,
                        action.payload.participantId
                    )
                    .pipe(
                        map((response: TeamTournament) =>
                            TournamentParticipantsActions.fetchTeamTournamentDetailsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentParticipantsActions.fetchTeamTournamentDetailsFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );
    fetchTeamTournamentDetailsByIdV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.fetchTeamTournamentDetailsV2),
            switchMap((action) =>
                this.tournamentParticipantService
                    .getTeamTournamentInfo(
                        action.payload.tournamentId,
                        action.payload.participantId
                    )
                    .pipe(
                        map((response: any) =>
                            TournamentParticipantsActions.fetchTeamTournamentDetailsV2Success({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentParticipantsActions.fetchTeamTournamentDetailsV2Failure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    fetchClosestTournamentMatch = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentParticipantsActions.fetchClosestTournamentMatch),
            switchMap((action) =>
                this.tournamentParticipantService
                    .fetchClosestMatchTournament(action.payload.tournamentId)
                    .pipe(
                        map((response: ClosestTournamentMatch) =>
                            TournamentParticipantsActions.fetchClosestTournamentMatchSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentParticipantsActions.fetchClosestTournamentMatchFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentParticipantResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentParticipantsActions.fetchTournamentParticipantResult
            ),
            switchMap((action) =>
                this.tournamentParticipantService
                    .fetchTournamentParticipantResult(
                        action.payload.tournamentId
                    )
                    .pipe(
                        map((response: TournamentResult) =>
                            TournamentParticipantsActions.fetchTournamentParticipantResultSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentParticipantsActions.fetchTournamentParticipantResultFailure(
                                    { payload: response }
                                )
                            )
                        )
                    )
            )
        )
    );
    fetchProfileBalanceAfterJoin$ = createEffect(() =>
    this.actions$.pipe(
        ofType(TournamentParticipantsActions.joinToATournamentSuccess),
        map(action => {
            const profileId = action.payload.profileId;
            return WalletActions.fetchProfilePaymentAccounts({
                payload: {
                    playerId: profileId,
                },
            });
        })
    )
);
}
